import React, { useState, useContext } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, Tooltip, Menu, MenuItem, Avatar, IconButton, Fade } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDispatch, useSelector } from "react-redux";
import { Auth } from 'aws-amplify';
import { logout } from '../action/auth';
import { useIntl } from 'react-intl';
import cpTheme from './theme.js';
import './ArtemyStyles.css';

// Legacy components & context
import NotificationDropdown from "../Notificationtalent";
import NotificationManagerIcon from "../Notificationmanagericon";
// import { LanguageContext } from '../LanguageContext';
import LanguageIcon from '@mui/icons-material/Language';
import SearchIcon from '@mui/icons-material/Search';

const getNavLinks = (userType) => {
  const links = [
    { id: 'hm', label: 'Home', path: '/' },
  ];

  if (userType === 'stakeholder') {
    links.push({ id: 'db', label: 'Dashboard', path: '/StakeholderDashboard' });
  } else if (userType === 'Candidate') {
    links.push({ id: 'db', label: 'Dashboard', path: '/candidate-dashboard' });
  } else if (userType === 'ClientAdmin' || userType === 'customer') {
    links.push({ id: 'db', label: 'Dashboard', path: '/subscriptions' });
  } else {
    links.push({ id: 'db', label: 'Dashboard', path: '/client-dashboard' });
  }

  // Add role-specific secondary links
  if (userType === 'Client') {
    links.push({ id: 'mp', label: 'My Pods', path: '/Team' });
  }

  return links;
};

const getSidebarSections = (user, intl) => {
  const userType = user?.data?.[0]?.type;
  const userManagerType = user?.data?.[0]?.manager_type;
  const userEmpId = user?.data?.[0]?.emp_id;

  // Determine user-specific top items
  let topItems = [
    { id: '/', label: intl.formatMessage({ id: 'home' }), icon: 'home' },
  ];
  
  // Dashboard item
  topItems.push({ id: '/control-plane', label: intl.formatMessage({ id: 'dashboard' }), icon: 'grid', badge: null });

  // Add Profile and Setting/Invite items based on user type to the top items
  if (userType === 'Candidate') {
    topItems.push({ id: '/Updateyourprofile', label: intl.formatMessage({ id: 'manageProfile' }), icon: 'profile' });
    topItems.push({ id: '/EditTalentSettingsForm', label: intl.formatMessage({ id: 'talentSettings', defaultMessage: 'Talent Settings' }), icon: 'settings' });
  } else if (userType === 'Client') {
    topItems.push({ id: '/client-profile', label: intl.formatMessage({ id: 'manageProfile' }), icon: 'profile' });
    topItems.push({ id: '/Popup', label: intl.formatMessage({ id: 'Invite', defaultMessage: 'Invite' }), icon: 'people' });
    topItems.push({ id: '/Setting', label: intl.formatMessage({ id: 'Setting', defaultMessage: 'Setting' }), icon: 'settings' });
  } else if (userType === 'stakeholder') {
    topItems.push({ id: '/stakeholder-profile', label: intl.formatMessage({ id: 'manageProfile' }), icon: 'profile' });
  }

  const commonSections = [
    {
      label: intl.formatMessage({ id: 'Overview', defaultMessage: 'Overview' }),
      items: topItems,
    },
    {
      label: intl.formatMessage({ id: 'Delivery Confidence', defaultMessage: 'Delivery Confidence' }),
      items: [
        { id: '/control-plane/confidence', label: intl.formatMessage({ id: 'Confidence Feed', defaultMessage: 'Confidence Feed' }), icon: 'clock', badge: '3', badgeColor: 'var(--artemy-yellow)' },
        { id: '/control-plane/trends', label: intl.formatMessage({ id: 'Trend Analysis', defaultMessage: 'Trend Analysis' }), icon: 'chart', badge: null },
        { id: '/control-plane/risks', label: intl.formatMessage({ id: 'Risk Registry', defaultMessage: 'Risk Registry' }), icon: 'alert', badge: '8', badgeColor: 'var(--artemy-red)' },
        { id: '/control-plane/intent', label: intl.formatMessage({ id: 'Intent Graph', defaultMessage: 'Intent Graph' }), icon: 'graph', badge: null },
        { id: '/control-plane/dora', label: intl.formatMessage({ id: 'doraMetrics' }), icon: 'bars', badge: null },
        { id: '/control-plane/sprints', label: intl.formatMessage({ id: 'Sprint Predictions', defaultMessage: 'Sprint Predictions' }), icon: 'wave', badge: null },
      ],
    },
  ];

  const podManagementItems = {
    Candidate: [
      { id: '/Podactivitytalent', label: intl.formatMessage({ id: 'podActivity' }), icon: 'activity' },
      { id: '/PodactivitytalentRetro', label: intl.formatMessage({ id: 'podRetro' }), icon: 'history' },
      { id: '/Createtasktalent', label: intl.formatMessage({ id: 'podTasks' }), icon: 'tasks' },
      { id: '/Pods', label: intl.formatMessage({ id: 'myPods' }), icon: 'people' },
      { id: '/KanbanboardTalent', label: intl.formatMessage({ id: 'podBoard' }), icon: 'board' },
      { id: '/PodMetricsTalentDetailview', label: intl.formatMessage({ id: 'podMetrics' }), icon: 'chart' },
      { id: '/OpenAi', label: intl.formatMessage({ id: 'podAssist' }), icon: 'help' },
      { id: '/Workflow', label: intl.formatMessage({ id: 'Build Workflow', defaultMessage: 'Build Workflow' }), icon: 'grid' },
      { id: '/Reviewsandratingcandidate', label: intl.formatMessage({ id: 'podFeedback' }), icon: 'comment' },
      { id: '/MeetingSummariesTableTalent', label: intl.formatMessage({ id: 'podsync' }), icon: 'sync' },
    ],
    Client: [
      { id: '/Team', label: intl.formatMessage({ id: 'myPods' }), icon: 'people' },
      { id: '/CreateSolution', label: intl.formatMessage({ id: 'createSolution', defaultMessage: 'Create Solution' }), icon: 'help' },
      { id: '/project-requirment', label: intl.formatMessage({ id: 'createArt', defaultMessage: 'Create Art' }), icon: 'bars' },
      { id: '/client-createpod', label: intl.formatMessage({ id: 'createPod', defaultMessage: 'Create Pod' }), icon: 'people' },
      { id: '/PodActivity', label: intl.formatMessage({ id: 'podActivity' }), icon: 'activity' },
      { id: '/ViewPodTasksManagerbyid', label: intl.formatMessage({ id: 'podTasks' }), icon: 'tasks' },
      { id: '/PodProductivity', label: intl.formatMessage({ id: 'POD Productivity', defaultMessage: 'POD Productivity' }), icon: 'grid' },
      { id: '/KanbanBoard', label: intl.formatMessage({ id: 'podBoard' }), icon: 'board' },
      { id: '/Podretromanager', label: intl.formatMessage({ id: 'podRetro' }), icon: 'history' },
      { id: '/PodMetricsmanager', label: intl.formatMessage({ id: 'podMetrics' }), icon: 'chart' },
      { id: '/OpenAi', label: intl.formatMessage({ id: 'podAssist' }), icon: 'help' },
      { id: '/PodInsights', label: intl.formatMessage({ id: 'podQuery', defaultMessage: 'Pod Query' }), icon: 'chart' },
      { id: '/PodAutomation', label: intl.formatMessage({ id: 'podAutomation' }), icon: 'grid' },
      { id: '/Workflow', label: intl.formatMessage({ id: 'Build Workflow', defaultMessage: 'Build Workflow' }), icon: 'grid' },
      { id: '/ReviewsandratingClient', label: intl.formatMessage({ id: 'podFeedback' }), icon: 'comment' },
      { id: '/MeetingSummariesTimeline', label: intl.formatMessage({ id: 'podsync' }), icon: 'sync' },
      ...(userManagerType === 'PartnerClient' && userEmpId === 1641
        ? [{ id: '/PartnerMetricsDashboard', label: intl.formatMessage({ id: 'partnerMetrics' }), icon: 'chart' }]
        : []
      )
    ],
    stakeholder: [
      { id: '/PodActivitystakeholderviewall', label: intl.formatMessage({ id: 'podActivity' }), icon: 'activity' },
      { id: '/PodMetricsmanager', label: intl.formatMessage({ id: 'podMetrics' }), icon: 'chart' },
    ]
  };

  const podManagementSection = {
    label: intl.formatMessage({ id: 'podManagement', defaultMessage: 'Pod Management' }),
    items: podManagementItems[userType] || [],
  };

  const remainingSections = [
    {
      label: intl.formatMessage({ id: 'Agent Pods', defaultMessage: 'Agent Pods' }),
      items: [
        { id: '/control-plane/pod-ops', label: intl.formatMessage({ id: 'Operational Pod', defaultMessage: 'Operational Pod' }), icon: 'cBlue', color: 'var(--artemy-accent)' },
        { id: '/control-plane/pod-product', label: intl.formatMessage({ id: 'Product Pod', defaultMessage: 'Product Pod' }), icon: 'cGreen', color: 'var(--artemy-green)' },
        { id: '/control-plane/pod-eng', label: intl.formatMessage({ id: 'Engineering Pod', defaultMessage: 'Engineering Pod' }), icon: 'cYellow', color: 'var(--artemy-yellow)' },
        { id: '/control-plane/pod-mgmt', label: intl.formatMessage({ id: 'Management Pod', defaultMessage: 'Management Pod' }), icon: 'cPurple', color: 'var(--artemy-purple)' },
        ...(userType === 'Client' ? [{ id: '/AgentsList', label: intl.formatMessage({ id: 'Agent Metrics', defaultMessage: 'Agent Metrics' }), icon: 'grid' }] : []),
        ...(userType === 'Candidate' ? [{ id: '/control-plane/approvals', label: intl.formatMessage({ id: 'Approver Dashboard', defaultMessage: 'Approver Dashboard' }), icon: 'approve' }] : [])
      ],
    },
    {
      label: intl.formatMessage({ id: 'Governance', defaultMessage: 'Governance' }),
      items: [
        { id: '/control-plane/policies', label: intl.formatMessage({ id: 'Policy Engine', defaultMessage: 'Policy Engine' }), icon: 'shield' },
        { id: '/control-plane/forensics', label: intl.formatMessage({ id: 'Forensics Ledger', defaultMessage: 'Forensics Ledger' }), icon: 'ledger' },
        { id: '/control-plane/confidence-engine', label: intl.formatMessage({ id: 'Confidence Engine', defaultMessage: 'Confidence Engine' }), icon: 'cGreen' },
        { id: '/control-plane/agent-confidence', label: intl.formatMessage({ id: 'Agent Confidence', defaultMessage: 'Agent Confidence' }), icon: 'cPurple' },
      ],
    },
  ];

  if (userType === 'stakeholder' || (userManagerType === 'PartnerClient' && userEmpId === 1641 && userType !== 'Client')) {
     // If not a Client but is a PartnerClient, we might still want to show the pod management section
     // But for now, let's just make sure PartnerClient gets the item if they are under Client role.
     // If they are NOT under Client role, we might need a separate logic.
  }

  if (userType === 'stakeholder') {
    return [
      {
        label: intl.formatMessage({ id: 'Overview', defaultMessage: 'Overview' }),
        items: topItems,
      },
      podManagementSection
    ];
  }

  return [...commonSections, podManagementSection, ...remainingSections];
};

const IC = {
  grid: (active) => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" /><rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity={active ? 0.8 : 0.5} /><rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity={active ? 0.8 : 0.5} /><rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity={active ? 1 : 0.8} /></svg>,
  clock: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" /><path d="M8 4v4l3 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>,
  chart: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 12L5 7 8 10 11 5 14 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  alert: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2L14 13H2L8 2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M8 6v3.5M8 11v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>,
  graph: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="5" cy="5" r="2" stroke="currentColor" strokeWidth="1.3" /><circle cx="11" cy="5" r="2" stroke="currentColor" strokeWidth="1.3" /><circle cx="8" cy="11" r="2" stroke="currentColor" strokeWidth="1.3" /><path d="M7 5h2M6.5 9.5L5 7M9.5 9.5L11 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>,
  bars: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 14V8M6 14V5M9 14V9M12 14V3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>,
  wave: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8h3l2-5 3 10 2-5 2 3 2-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  cBlue: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="3" stroke="var(--artemy-accent)" strokeWidth="1.5" /><circle cx="8" cy="8" r="6" stroke="var(--artemy-accent)" strokeWidth="1" opacity=".4" /></svg>,
  cGreen: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="3" stroke="var(--artemy-green)" strokeWidth="1.5" /><circle cx="8" cy="8" r="6" stroke="var(--artemy-green)" strokeWidth="1" opacity=".4" /></svg>,
  cYellow: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="3" stroke="var(--artemy-yellow)" strokeWidth="1.5" /><circle cx="8" cy="8" r="6" stroke="var(--artemy-yellow)" strokeWidth="1" opacity=".4" /></svg>,
  cPurple: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="3" stroke="var(--artemy-purple)" strokeWidth="1.5" /><circle cx="8" cy="8" r="6" stroke="var(--artemy-purple)" strokeWidth="1" opacity=".4" /></svg>,
  shield: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1L2 4v5c0 3 3 5 6 6 3-1 6-3 6-6V4L8 1z" stroke="currentColor" strokeWidth="1.4" /></svg>,
  ledger: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 2h10a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.3" /><path d="M5 6h6M5 9h4M5 12h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>,
  home: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 7l6-5 6 5v7a1 1 0 01-1 1H3a1 1 0 01-1-1V7z" stroke="currentColor" strokeWidth="1.4" /><path d="M6 15V9h4v6" stroke="currentColor" strokeWidth="1.4" /></svg>,
  profile: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.4" /><path d="M3 13c0-2 2-4 5-4s5 2 5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>,
  activity: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M2 8h9M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>,
  history: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 2a6 6 0 106 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /><path d="M14 4v4h-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /><path d="M8 5v3l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>,
  tasks: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="3" y="3" width="10" height="10" rx="1" stroke="currentColor" strokeWidth="1.4" /><path d="M6 8l1.5 1.5L10 6.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  people: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 13c0-1.5 1-3 3-3s3 1.5 3 3M11 13c0-1.5 1-3 3-3M7 6a2 2 0 11-4 0 2 2 0 014 0zM13 7a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" stroke="currentColor" strokeWidth="1.4" /></svg>,
  board: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 3h3v10H2V3zm4.5 0h3v10h-3V3zm4.5 0h3v10h-3V3z" stroke="currentColor" strokeWidth="1.4" /></svg>,
  help: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" /><path d="M6.5 6.5C6.5 5.5 7.5 5 8 5s1.5.5 1.5 1.5c0 1-1 1.5-1.5 2V10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /><circle cx="8" cy="12" r="0.5" fill="currentColor" /></svg>,
  comment: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4a1 1 0 011-1h10a1 1 0 011 1v7a1 1 0 01-1 1H5l-3 3V4z" stroke="currentColor" strokeWidth="1.4" /></svg>,
  sync: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 8a6 6 0 0110-4.5L14 6M14 8a6 6 0 01-10 4.5L2 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /><path d="M10 3.5h4v4M6 12.5H2v-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  settings: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 10a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="1.4" /><path d="M12.5 8a4.5 4.5 0 01-9 0" stroke="currentColor" strokeWidth="1.4" opacity=".4" /><path d="M8 2v2M8 12v2M2 8h2M12 8h2M3.7 3.7l1.4 1.4M10.9 10.9l1.4 1.4M3.7 12.3l1.4-1.4M10.9 5.1l1.4-1.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>,
  approve: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" /><path d="M5 8.5l2 2 4-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
};

function NavItem({ link, active, onClick }) {
  return (
    <li>
      <Box
        component="span"
        onClick={onClick}
        className={active ? 'active' : ''}
        sx={{
          fontSize: 13, color: active ? 'var(--artemy-text-primary)' : 'var(--artemy-text-secondary)', cursor: 'pointer',
          padding: '6px 12px', borderRadius: '6px',
          background: active ? 'var(--artemy-bg-elevated)' : 'transparent',
          transition: 'all .15s',
          display: 'block',
          '&:hover': { color: 'var(--artemy-text-primary)', background: 'var(--artemy-bg-elevated)' },
        }}
      >
        {link.label}
      </Box>
    </li>
  );
}

function ControlPlaneLayout({ children, currentLocale = 'en', changeLocale }) {
  const history = useHistory();
  const location = useLocation();
  const path = location.pathname;
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('artemySidebarCollapsed');
    return saved === 'true';
  });

  const sidebarRef = React.useRef(null);

  React.useEffect(() => {
    if (sidebarRef.current) {
      const savedScroll = sessionStorage.getItem('artemySidebarScroll');
      if (savedScroll) {
        sidebarRef.current.scrollTop = parseInt(savedScroll, 10);
      }
    }
  }, []);

  const handleSidebarScroll = (e) => {
    sessionStorage.setItem('artemySidebarScroll', e.target.scrollTop);
  };

  const toggleSidebar = () => {
    const newVal = !isCollapsed;
    setIsCollapsed(newVal);
    localStorage.setItem('artemySidebarCollapsed', String(newVal));
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const userType = user?.data?.[0]?.type;
  const navLinks = getNavLinks(userType);

  const activeNav = navLinks.find((l) =>
    l.path === path || (l.path !== '/' && path.startsWith(l.path))
  )?.id || 'cp';

  // const { currentLocale, changeLocale } = useContext(LanguageContext);
  const intl = useIntl();

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogOut = async (e) => {
    e.preventDefault();
    try {
      await Auth.signOut();
      dispatch(logout());
      history.push("/login");
    } catch (error) {
      console.log(error.message);
    }
  };

  const userEmail = user?.data?.[0]?.email || 'User';
  const initial = userEmail.charAt(0).toUpperCase();

  const getProfileUrl = () => {
    const profileUrls = {
      Candidate: "/Updateyourprofile",
      Client: "/client-profile",
      stakeholder: "/stakeholder-profile",
      PartnerGuest: "/partnerguest-profile",
      Provider: "/provider-profile",
      ClientAdmin: "/customer-profile"
    };
    return profileUrls[userType] || "/manage-profile";
  };

  return (
    <ThemeProvider theme={cpTheme}>
      <CssBaseline />
      <div className={`artemy-root ${isCollapsed ? 'sidebar-collapsed' : ''}`} style={{ background: '#08090d' }}>
        <div className="artemy-grid-bg" />

        {/* Navigation */}
        <nav className="artemy-nav">
          <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
            <div className="artemy-logo" style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => history.push('/control-plane')}>
              <svg width="26" height="26" viewBox="0 0 28 28" fill="none">
                <rect x="3" y="3" width="10" height="10" rx="2.5" fill="#3d7fff" opacity=".9" />
                <rect x="15" y="3" width="10" height="10" rx="2.5" fill="#3d7fff" opacity=".4" />
                <rect x="3" y="15" width="10" height="10" rx="2.5" fill="#3d7fff" opacity=".4" />
                <rect x="15" y="15" width="10" height="10" rx="2.5" fill="#00d4aa" opacity=".85" />
                <circle cx="14" cy="14" r="3" fill="#fff" opacity=".9" />
              </svg>
              <span className="artemy-logo-text" style={{ fontFamily: 'var(--artemy-font-display)', fontWeight: 700, fontSize: 17, color: 'var(--artemy-text-primary)', letterSpacing: '-.3px' }}>
                artemy<span style={{ color: 'var(--artemy-accent)' }}>.ai</span>
              </span>
            </div>
            <ul style={{ display: 'flex', gap: 2, listStyle: 'none', padding: 0, margin: 0 }}>
              {navLinks.map((l) => (
                <NavItem
                  key={l.id}
                  link={l}
                  active={activeNav === l.id}
                  onClick={() => history.push(l.path)}
                />
              ))}
            </ul>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: 400 }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: 'var(--artemy-bg-base)',
              border: '1px solid var(--artemy-border)',
              borderRadius: 12,
              padding: '6px 14px',
              width: '100%',
              gap: 10,
              transition: 'all .2s',
              '&:hover': { borderColor: 'var(--artemy-accent)' }
            }}>
              <SearchIcon sx={{ fontSize: 18, color: 'var(--artemy-text-dim)' }} />
              <input
                type="text"
                placeholder="Search Agent Pods, Risks or Policies..."
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  color: 'var(--artemy-text-primary)',
                  fontSize: 13,
                  width: '100%',
                  fontFamily: 'inherit'
                }}
              />
              <span style={{
                fontFamily: 'var(--artemy-font-mono)',
                fontSize: 10,
                color: 'var(--artemy-text-dim)',
                background: 'var(--artemy-bg-elevated)',
                padding: '2px 6px',
                borderRadius: 4,
                border: '1px solid var(--artemy-border)'
              }}>⌘K</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Language Switcher */}
            <Tooltip title={intl.formatMessage({ id: 'changeLanguage' })}>
              <IconButton
                onClick={() => changeLocale(currentLocale === 'en' ? 'fr' : 'en')}
                sx={{ color: 'var(--artemy-text-secondary)', '&:hover': { color: 'var(--artemy-accent)' } }}
              >
                <LanguageIcon sx={{ fontSize: 20 }} />
                <span style={{ fontSize: 10, marginLeft: 4, fontWeight: 700, fontFamily: 'var(--artemy-font-mono)' }}>
                  {currentLocale.toUpperCase()}
                </span>
              </IconButton>
            </Tooltip>

            {/* Notifications */}
            {userType === "Client" ? (
              <NotificationManagerIcon />
            ) : userType === "Candidate" ? (
              <NotificationDropdown />
            ) : null}

            <div style={{ borderLeft: '1px solid var(--artemy-border)', height: 24, margin: '0 4px' }} />

            <Box onClick={handleMenuClick} sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', padding: '4px 8px', borderRadius: 1.5, '&:hover': { background: 'var(--artemy-bg-elevated)' }, transition: 'background .2s' }}>
              <Avatar sx={{ width: 28, height: 28, fontSize: 13, background: 'var(--artemy-accent)', color: '#fff', fontWeight: 600 }}>{initial}</Avatar>
              <Box sx={{ display: { xs: 'none', md: 'block' }, fontSize: 13, color: 'var(--artemy-text-primary)' }}>{userEmail}</Box>
            </Box>
            <Menu
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  background: 'var(--artemy-bg-elevated)',
                  border: '1px solid var(--artemy-border)',
                  color: 'var(--artemy-text-primary)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  '& .MuiMenuItem-root': { fontSize: 13, py: 1, px: 2, '&:hover': { background: 'var(--artemy-border)' } }
                }
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={() => { handleMenuClose(); history.push(getProfileUrl()); }}>Manage Profile</MenuItem>
              <MenuItem onClick={() => { handleMenuClose(); history.push('/forgotpassword'); }}>Reset Password</MenuItem>
              <MenuItem sx={{ color: 'var(--artemy-red) !important' }} onClick={handleLogOut}>Log Out</MenuItem>
            </Menu>
          </div>
        </nav>

        <div className="artemy-body">
          {/* Sidebar */}
          <aside 
            ref={sidebarRef}
            onScroll={handleSidebarScroll}
            className={`artemy-sidebar ${isCollapsed ? 'collapsed' : ''}`}
          >
            <div className="artemy-sidebar-toggle">
              <IconButton 
                onClick={toggleSidebar}
                size="small"
                sx={{ 
                  color: 'var(--artemy-text-dim)',
                  background: 'var(--artemy-bg-elevated)',
                  border: '1px solid var(--artemy-border)',
                  '&:hover': { color: 'var(--artemy-accent)', borderColor: 'var(--artemy-accent)' },
                  transition: 'all .2s'
                }}
              >
                {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
              </IconButton>
            </div>
            {getSidebarSections(user, intl).map((sec) => (
              <div key={sec.label} style={{ marginBottom: 22 }}>
                {!isCollapsed && <div className="artemy-sb-label">{sec.label}</div>}
                {sec.items.map((item) => {
                  const active = item.id === '/' ? path === '/' : (path === item.id || (item.id !== '/control-plane' && path.startsWith(item.id)));
                  return (
                    <Tooltip 
                      key={item.id} 
                      title={isCollapsed ? item.label : ""} 
                      placement="right" 
                      arrow
                    >
                      <div
                        className={`artemy-sb-item ${active ? 'active' : ''}`}
                        onClick={() => history.push(item.id)}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', opacity: active ? 1 : 0.8, color: item.color || (active ? 'var(--artemy-accent)' : 'inherit') }}>
                          {IC[item.icon] ? IC[item.icon](active) : null}
                        </span>
                        {!isCollapsed && <span style={{ flex: 1 }}>{item.label}</span>}
                        {!isCollapsed && item.badge && (
                          <span style={{
                            marginLeft: 'auto', fontFamily: 'var(--artemy-font-mono)', fontSize: 10, padding: '1px 7px', borderRadius: 10,
                            background: `${item.badgeColor}22`, color: item.badgeColor
                          }}>
                            {item.badge}
                          </span>
                        )}
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            ))}
          </aside>

          {/* Main content Area */}
          <main className={`artemy-main ${isCollapsed ? 'expanded' : ''}`}>
            {children}
          </main>
        </div>
      </div>
    </ThemeProvider>
  );
}

export default ControlPlaneLayout;
